import { addDays, format, isAfter, parseISO } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import type {
  AvailabilityCourtPayload,
  GetResponsePayload,
} from 'constants/payloads/partnerBookingsAvailability';
import { BookableCourt, BookingDate, BookingSlot, VenueBookingAvailability } from './types';
import { slotLabel } from './utils';

/** How far the date strip looks ahead when the club sets no window. */
export const DAYS_AHEAD = 14;
const LATE_HOURS_START = 22;
const LATE_HOURS_END = 6;

const toMinutes = (hhmm: string) => {
  const [hours, minutes] = hhmm.split(':').map(Number);
  return hours * 60 + minutes;
};
const toHhmm = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

const covers = (court: AvailabilityCourtPayload, start: number, end: number) =>
  court.free.some((span) => toMinutes(span.startLocal) <= start && toMinutes(span.endLocal) >= end);

/**
 * One chip per booking increment across the club's day. A chip is available when
 * any court is free for the whole increment; the court itself is chosen later.
 */
export const buildSlots = (day: GetResponsePayload): BookingSlot[] => {
  const spans = day.courts.flatMap((court) => court.free);
  if (spans.length === 0) {
    return [];
  }
  const open = Math.min(...spans.map((span) => toMinutes(span.startLocal)));
  const close = Math.max(...spans.map((span) => toMinutes(span.endLocal)));
  const slots: BookingSlot[] = [];

  for (let start = open; start + day.slotMinutes <= close; start += day.slotMinutes) {
    const end = start + day.slotMinutes;
    const startLocal = toHhmm(start);
    const hour = Math.floor(start / 60);
    slots.push({
      id: `${day.date}-${startLocal.replace(':', '')}`,
      startAt: fromZonedTime(`${day.date}T${startLocal}:00`, day.timezone).toISOString(),
      endAt: fromZonedTime(`${day.date}T${toHhmm(end)}:00`, day.timezone).toISOString(),
      label: slotLabel(startLocal, toHhmm(end)),
      isAvailable: day.courts.some((court) => covers(court, start, end)),
      // Lead-time discounts are priced on the server and not shown per slot yet.
      discountPercent: null,
      isLateHours: hour >= LATE_HOURS_START || hour < LATE_HOURS_END,
    });
  }
  return slots;
};

export const toBookableCourt = (court: AvailabilityCourtPayload): BookableCourt => ({
  id: court.providerCourtId ?? court.providerResourceId,
  providerResourceId: court.providerResourceId,
  name: court.name,
  priceUnitAmount: court.fromPriceUnitAmount ?? 0,
  listPriceUnitAmount: null,
  currency: court.currency,
  // The club's system names courts but says nothing else about them yet.
  isIndoor: /indoor/i.test(court.name),
  netType: null,
  surfaceLabel: null,
});

/** Courts free for the whole stretch the player picked, in venue-local HH:mm. */
export const getCourtsFreeFor = (
  day: GetResponsePayload | undefined,
  startLocal: string,
  endLocal: string,
): BookableCourt[] =>
  (day?.courts ?? [])
    .filter((court) => covers(court, toMinutes(startLocal), toMinutes(endLocal)))
    .map(toBookableCourt);

export const buildDates = (
  todayLocal: string,
  lastBookableDate: string | null,
  bookableDays: string[] | null = null,
): BookingDate[] => {
  const dates: BookingDate[] = [];
  for (let index = 0; index < DAYS_AHEAD; index += 1) {
    const day = addDays(parseISO(todayLocal), index);
    if (lastBookableDate && isAfter(day, parseISO(lastBookableDate))) {
      break;
    }
    if (bookableDays && !bookableDays.includes(format(day, 'EEEE').toUpperCase())) {
      continue;
    }
    dates.push({
      localDate: format(day, 'yyyy-MM-dd'),
      weekdayLabel: format(day, 'EEE'),
      dayLabel: format(day, 'd'),
      hasDeals: false,
      isToday: index === 0,
    });
  }
  return dates;
};

export const getTodayLocal = (timezone: string, now = new Date()) =>
  formatInTimeZone(now, timezone, 'yyyy-MM-dd');

/** Assembles the shape the booking surfaces render from whatever days have loaded. */
export const buildAvailability = (params: {
  venueId: string;
  venueName: string;
  timezone: string;
  days: Record<string, GetResponsePayload>;
  now?: Date;
}): VenueBookingAvailability => {
  const loaded = Object.values(params.days);
  const first = loaded[0];
  const timezone = first?.timezone ?? params.timezone;
  const slotsByDate = Object.fromEntries(loaded.map((day) => [day.date, buildSlots(day)]));
  const courtsById = new Map(
    loaded.flatMap((day) => day.courts.map((court) => [court.providerResourceId, court] as const)),
  );

  return {
    venueId: params.venueId,
    venueName: params.venueName,
    timezone,
    slotMinutes: first?.slotMinutes ?? 30,
    minimumMinutes: first?.minDurationMinutes ?? 60,
    dates: buildDates(
      getTodayLocal(timezone, params.now),
      first?.lastBookableDate ?? null,
      first?.bookableDays ?? null,
    ),
    bookableHoursLabel: first?.bookableHoursLabel ?? null,
    slotsByDate,
    courts: Array.from(courtsById.values()).map(toBookableCourt),
  };
};
